import type {
  ReasoningContentEvent,
  RuntimeEvent,
  TextMessageContentEvent,
  ToolCallArgsEvent,
} from './runtime/events';

/**
 * StreamingBuffer coalesces high-frequency delta events before emitting them
 * to a caller-provided callback.
 *
 * Streaming deltas (text-message-content, reasoning-content, tool-call-args)
 * arrive faster than React can render. The buffer collects them and flushes
 * once per animation frame, coalescing consecutive deltas for the same
 * message into a single callback.
 *
 * Non-streaming events (lifecycle, start/end markers) flush the pending
 * buffer first, then emit immediately to preserve ordering.
 */
export class StreamingBuffer {
  private pending: StreamingDelta[] = [];
  private rafId: number | null = null;
  private disposed = false;

  constructor(private readonly onEvent: (event: RuntimeEvent) => void) {}

  /**
   * dispose synchronously flushes the buffer and closes it; call when the
   * stream ends. After calling, the buffer is no longer usable.
   */
  dispose(): void {
    this.flush();
    this.disposed = true;
  }

  /** flush coalesces and emits all pending deltas. */
  flush(): void {
    this.cancelScheduledFlush();

    if (this.pending.length === 0) {
      return;
    }

    const coalesced = coalesceStreamingEvents(this.pending);
    this.pending = [];

    for (const event of coalesced) {
      this.onEvent(event);
    }
  }

  /** push enqueues an event. Streaming deltas buffer; others flush+emit. */
  push(event: RuntimeEvent): void {
    if (this.disposed) {
      return;
    }

    if (isStreamingDelta(event)) {
      this.pending.push(event);
      this.scheduleFlush();
      return;
    }

    // Non-streaming event; flush pending first to preserve order.
    this.flush();
    this.onEvent(event);
  }

  private cancelScheduledFlush(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private scheduleFlush(): void {
    if (this.rafId === null) {
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.flush();
      });
    }
  }
}

/**
 * coalesceStreamingEvents collects consecutive streaming events that share the
 * same `messageId` or `toolCallId`. Adjacent deltas of the same type and target
 * are merged by concatenating their `delta` strings.
 */
export function coalesceStreamingEvents(
  events: StreamingDelta[]
): StreamingDelta[] {
  if (events.length <= 1) {
    return [...events];
  }

  const result: StreamingDelta[] = [];
  let current = events[0];

  for (let i = 1; i < events.length; i++) {
    const next = events[i];

    if (canCoalesce(current, next)) {
      current = mergeDeltas(current, next);
    } else {
      result.push(current);
      current = next;
    }
  }

  result.push(current);
  return result;
}

export function isStreamingDelta(event: RuntimeEvent): event is StreamingDelta {
  return (
    event.type === 'text-message-content' ||
    event.type === 'reasoning-content' ||
    event.type === 'tool-call-args'
  );
}

type StreamingDelta =
  | TextMessageContentEvent
  | ReasoningContentEvent
  | ToolCallArgsEvent;

function canCoalesce(a: StreamingDelta, b: StreamingDelta): boolean {
  if (a.type !== b.type) {
    return false;
  }
  switch (a.type) {
    case 'text-message-content':
    case 'reasoning-content':
      return a.messageId === (b as typeof a).messageId;
    case 'tool-call-args':
      return a.toolCallId === (b as typeof a).toolCallId;
  }
}

function mergeDeltas(a: StreamingDelta, b: StreamingDelta): StreamingDelta {
  return {...a, delta: a.delta + b.delta} as StreamingDelta;
}
